import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';import AdminSidebar from './Components/AdminSidebar';
import { FaSearch, FaBars, FaTh, FaEdit, FaTimes, FaPlus, FaTrash, FaImage } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import lead6 from '../images/no-bg/6_lead.png';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const ADMIN_TOKEN_KEY = 'ADMIN_TOKEN';

// Team format: "Name || Role || Description or training/certificate" (one line per member)
const TEAM_FIELD_SEP = ' || ';
function parseTeamLine(str) {
  const s = String(str).trim();
  if (!s) return { name: '', role: '', description: '' };
  if (s.includes(TEAM_FIELD_SEP)) {
    const parts = s.split(TEAM_FIELD_SEP).map((p) => p.trim());
    return {
      name: parts[0] || '',
      role: parts[1] || '',
      description: parts.slice(2).join(TEAM_FIELD_SEP).trim() || '',
    };
  }
  const colonIndex = s.indexOf(':');
  if (colonIndex > 0) {
    return {
      role: s.substring(0, colonIndex).trim(),
      name: s.substring(colonIndex + 1).trim(),
      description: '',
    };
  }
  return { name: s, role: '', description: '' };
}
function teamToTeamMembers(team) {
  if (!team || !Array.isArray(team)) return [];
  return team.map((item) => {
    if (typeof item === 'object' && item !== null) {
      return {
        name: item.name || '',
        role: item.role || item.position || '',
        description: item.description || '',
      };
    }
    return parseTeamLine(String(item));
  });
}
function teamMembersToArray(teamMembers) {
  if (!teamMembers || !Array.isArray(teamMembers)) return [];
  const emptyRow = ['', '', ''].join(TEAM_FIELD_SEP);
  return teamMembers
    .map((m) => [(m.name || '').trim(), (m.role || '').trim(), (m.description || '').trim()].join(TEAM_FIELD_SEP))
    .filter((row) => row.trim() !== '' && row !== emptyRow);
}

const SERVICE_INTEREST_LABELS = {
  'administrative-support': 'Administrative Support',
  'legal-compliance-support': 'Legal & Compliance Support',
  'project-management-support': 'Project Management Support',
  'client-relations-communication': 'Client Relations & Communication',
  'financial-planning': 'Financial Planning',
  'quantity-surveying-estimating': 'Quantity Surveying & Estimating',
  'procurement-vendor-management': 'Procurement & Vendor Management',
  'social-media-content-management': 'Social Media & Content Management',
};

// Sortable Employee Card Component
const SortableEmployeeCard = ({ employee, index, onCardClick, isPlaceholder, onAddClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: employee ? employee.id : `placeholder-${index}`,
    disabled: isPlaceholder,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isPlaceholder) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        onClick={onAddClick}
        className="rounded-[12px] p-6 relative overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.45)] cursor-pointer hover:opacity-90 transition-opacity w-full min-h-[260px] border-2 border-dashed border-gray-600 bg-[#1D1C21] flex items-center justify-center"
      >
        <div className="flex flex-col items-center justify-center text-gray-400 font-NeueMontreal select-none cursor-default">
          <FaPlus className="w-7 h-7 mb-2" />
          <p className="text-sm">Add Employee</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        background: 'linear-gradient(135deg, #E68A40 0%, #D97C37 55%, #BF5B19 100%)',
      }}
      onClick={() => onCardClick(employee)}
      className="rounded-[12px] relative overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.45)] cursor-pointer hover:opacity-90 
      transition-opacity w-full min-h-[260px] font-NeueMontreal select-none cursor-default"
    >
      <div
        {...attributes}
        {...listeners}
        className="absolute top-4 right-4 text-white hover:text-gray-200 z-10 cursor-grab active:cursor-grabbing"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </div>
      <button 
        onClick={(e) => {
          e.stopPropagation();
          onCardClick(employee);
        }}
        className="absolute top-4 right-12 text-white hover:text-gray-200 z-10"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </button>
      <div className="flex h-full gap-4 pt-6 pl-6 pr-6 pb-6 relative">
        <div className="w-2/5 flex flex-col justify-center text-left text-white z-10">
          <h3 className="text-2xl font-bold leading-tight mb-3 break-words">{employee.name}</h3>
          <p className="text-sm font-medium">{employee.profession}</p>
        </div>
      </div>
      <div className="absolute bottom-0 right-0 w-3/5 top-0 flex items-end justify-start overflow-hidden pt-10 pl-4"> 
        {/* pt-10 is the padding top to move the image down in the employee card */}
        <img 
          src={employee.image} 
          alt={employee.name}
          className="h-full w-auto object-cover object-bottom"
          style={{ objectPosition: 'left center', maxWidth: '100%' }}
        />
      </div>
    </div>
  );
};

const Employees = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeLink, setActiveLink] = useState('Employees');
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedEmployee, setEditedEmployee] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    profession: '',
    email: '',
    bio: '',
    teamMembers: [],
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [selectedInquiryId, setSelectedInquiryId] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [isLoadingInquiries, setIsLoadingInquiries] = useState(false);
  const [isSendingInquiry, setIsSendingInquiry] = useState(false);
  const [authToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [isUpdatingPositions, setIsUpdatingPositions] = useState(false);
  const [notification, setNotification] = useState(null);
  const [imageChoiceModal, setImageChoiceModal] = useState({ show: false, file: null });

  // Custom notification function
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, id: Date.now() });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Logout handler
  const handleLogout = () => {
    navigate('/adminlogin');
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Handle navigation from sidebar
  const handleNavigation = (linkName, route) => {
    setActiveLink(linkName);
    navigate(route);
  };

  // Dynamic widths
  const fullWidth = '360px';
  const collapsedWidth = '140px';

  // Fetch employees from backend
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/employees`);
      const data = await response.json();
      
      if (data.success) {
        // Convert MongoDB _id to id and ensure image has fallback
        // Sort by position for consistent ordering
        const formattedEmployees = data.employees
          .map(emp => ({
            ...emp,
            id: emp._id || emp.id,
            image: emp.image || lead6, // Fallback to default image if none
          }))
          .sort((a, b) => {
            // Sort by position first, then by createdAt as fallback
            if (a.position !== undefined && b.position !== undefined) {
              return a.position - b.position;
            }
            if (a.position !== undefined) return -1;
            if (b.position !== undefined) return 1;
            return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
          });
        setEmployees(formattedEmployees);
      } else {
        setError('Failed to fetch employees');
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch employees on component mount
  useEffect(() => {
    fetchEmployees();
  }, []);

  // Handle card click
  const handleCardClick = (employee) => {
    setIsClosing(false); // Reset closing state when opening new card
    // Ensure employee has both id and _id for compatibility
    const formattedEmployee = {
      ...employee,
      id: employee.id || employee._id,
      _id: employee._id || employee.id,
    };
    setSelectedEmployee(formattedEmployee);
    setIsEditMode(false);
  };

  // Handle edit button click
  const handleEditClick = () => {
    setIsClosing(false); // Reset closing state when switching to edit
    // Ensure employee has both id and _id for compatibility
    const formattedEmployee = {
      ...selectedEmployee,
      id: selectedEmployee?.id || selectedEmployee?._id,
      _id: selectedEmployee?._id || selectedEmployee?.id,
      teamMembers: teamToTeamMembers(selectedEmployee?.team),
    };
    setEditedEmployee(formattedEmployee);
    setIsEditMode(true);
  };

  // Handle close modal with animation
  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedEmployee(null);
      setIsEditMode(false);
      setIsAddMode(false);
      setEditedEmployee(null);
      setNewEmployee({ name: '', profession: '', email: '', bio: '', teamMembers: [] });
      setImagePreview(null);
      setIsClosing(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }, 300); // Match animation duration
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    // Validate required fields
    if (!editedEmployee.name?.trim() || !editedEmployee.profession?.trim() || !editedEmployee.email?.trim()) {
      showNotification('Please fill in name, profession, and email', 'error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editedEmployee.email.trim())) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    const teamArray = teamMembersToArray(editedEmployee.teamMembers || []);

    try {
      const response = await fetch(`${API_BASE_URL}/employees/${editedEmployee.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editedEmployee.name,
          profession: editedEmployee.profession,
          email: editedEmployee.email,
          bio: editedEmployee.bio,
          team: teamArray,
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        const updatedEmployee = {
          ...data.employee,
          id: data.employee._id || data.employee.id,
          image: data.employee.image || lead6,
        };
        setSelectedEmployee(updatedEmployee);
        
        // Update the employees list
        setEmployees(prevEmployees =>
          prevEmployees.map(emp =>
            emp.id === updatedEmployee.id ? updatedEmployee : emp
          )
        );
        
        setIsEditMode(false);
        showNotification('Employee updated successfully!', 'success');
      } else {
        showNotification('Failed to update employee. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error updating employee:', err);
      showNotification('Failed to update employee. Please try again.', 'error');
    }
  };

  // Handle picture upload (only for existing employees - edit/view mode)
  const handlePictureChange = async (event) => {
    // Don't handle picture upload in add mode - it's handled in handleAddEmployee
    if (isAddMode) {
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image file', 'error');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('Image size should be less than 10MB', 'error');
      return;
    }

    // Get employee ID - check both editedEmployee and selectedEmployee
    let employeeId = null;
    if (isEditMode && editedEmployee) {
      employeeId = editedEmployee.id || editedEmployee._id;
    } else if (selectedEmployee) {
      employeeId = selectedEmployee.id || selectedEmployee._id;
    }

    // Validate employee ID
    if (!employeeId) {
      showNotification('Cannot upload picture: Employee ID is missing. Please select an employee first.', 'error');
      console.error('Employee ID is missing:', { isEditMode, editedEmployee, selectedEmployee });
      return;
    }

    // In edit mode: show choice modal (original vs remove background) before uploading
    if (isEditMode && editedEmployee) {
      setImageChoiceModal({ show: true, file });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // View mode: upload with background removal (default)
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('image', file);
      formData.append('removeBackground', 'true');

      const response = await fetch(`${API_BASE_URL}/employees/${employeeId}/picture`, {
        method: 'PATCH',
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        const updatedEmployee = {
          ...data.employee,
          id: data.employee._id || data.employee.id,
          image: data.employee.image || lead6,
        };
        setSelectedEmployee(updatedEmployee);
        setEmployees(prevEmployees =>
          prevEmployees.map(emp => (emp.id === updatedEmployee.id ? updatedEmployee : emp))
        );
        showNotification('Picture updated successfully!', 'success');
      } else {
        showNotification('Failed to upload picture. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error uploading picture:', err);
      showNotification('Failed to upload picture. Please try again.', 'error');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Upload with chosen option (original or remove background) from edit modal
  const handleConfirmImageChoice = async (removeBackground) => {
    const { file } = imageChoiceModal;
    if (!file || !editedEmployee) return;

    const employeeId = editedEmployee.id || editedEmployee._id;
    if (!employeeId) {
      showNotification('Employee ID is missing.', 'error');
      setImageChoiceModal({ show: false, file: null });
      return;
    }

    setUploadingImage(true);
    setImageChoiceModal({ show: false, file: null });

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('removeBackground', removeBackground ? 'true' : 'false');

      const response = await fetch(`${API_BASE_URL}/employees/${employeeId}/picture`, {
        method: 'PATCH',
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        const updatedEmployee = {
          ...data.employee,
          id: data.employee._id || data.employee.id,
          image: data.employee.image || lead6,
        };
        setEditedEmployee(updatedEmployee);
        setEmployees(prevEmployees =>
          prevEmployees.map(emp => (emp.id === updatedEmployee.id ? updatedEmployee : emp))
        );
        showNotification('Picture updated successfully!', 'success');
      } else {
        showNotification(data.message || 'Failed to upload picture. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error uploading picture:', err);
      showNotification('Failed to upload picture. Please try again.', 'error');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle change photo button click
  const handleChangePhotoClick = () => {
    fileInputRef.current?.click();
  };

  // Team member handlers (edit form)
  const handleTeamMemberChange = (index, field, value) => {
    setEditedEmployee((prev) => {
      const next = [...(prev.teamMembers || [])];
      if (!next[index]) next[index] = { name: '', role: '', description: '' };
      next[index] = { ...next[index], [field]: value };
      return { ...prev, teamMembers: next };
    });
  };
  const handleAddTeamMember = () => {
    setEditedEmployee((prev) => ({
      ...prev,
      teamMembers: [...(prev.teamMembers || []), { name: '', role: '', description: '' }],
    }));
  };
  const handleRemoveTeamMember = (index) => {
    setEditedEmployee((prev) => {
      const next = (prev.teamMembers || []).filter((_, i) => i !== index);
      return { ...prev, teamMembers: next };
    });
  };

  // Team member handlers (add form)
  const handleNewTeamMemberChange = (index, field, value) => {
    setNewEmployee((prev) => {
      const next = [...(prev.teamMembers || [])];
      if (!next[index]) next[index] = { name: '', role: '', description: '' };
      next[index] = { ...next[index], [field]: value };
      return { ...prev, teamMembers: next };
    });
  };
  const handleAddNewTeamMember = () => {
    setNewEmployee((prev) => ({
      ...prev,
      teamMembers: [...(prev.teamMembers || []), { name: '', role: '', description: '' }],
    }));
  };
  const handleRemoveNewTeamMember = (index) => {
    setNewEmployee((prev) => ({
      ...prev,
      teamMembers: (prev.teamMembers || []).filter((_, i) => i !== index),
    }));
  };

  // Handle add button click
  const handleAddClick = () => {
    // Check if employee limit is reached (8 employees max)
    if (employees.length >= 8) {
      showNotification('Maximum employee limit reached. You can only have 8 employees. Please delete an employee before adding a new one.', 'error');
      return;
    }

    setIsClosing(false);
    setNewEmployee({
      name: '',
      profession: '',
      email: '',
      bio: '',
      teamMembers: [],
    });
    setImagePreview(null);
    setIsAddMode(true);
    setSelectedEmployee(null);
    setIsEditMode(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle add employee
  const handleAddEmployee = async () => {
    // Validate required fields
    if (!newEmployee.name.trim() || !newEmployee.profession.trim() || !newEmployee.email.trim()) {
      showNotification('Please fill in name, profession, and email', 'error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmployee.email.trim())) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    // Validate that image is provided (required field)
    const fileInput = fileInputRef.current;
    if (!fileInput || !fileInput.files || !fileInput.files[0]) {
      showNotification('Please add a picture. Picture is required.', 'error');
      return;
    }

    // Validate file type
    const file = fileInput.files[0];
    if (!file.type.startsWith('image/')) {
      showNotification('Please select an image file', 'error');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('Image size should be less than 10MB', 'error');
      return;
    }

    try {
      setUploadingImage(true);
      
      const teamArray = teamMembersToArray(newEmployee.teamMembers || []);

      const formData = new FormData();
      formData.append('name', newEmployee.name);
      formData.append('profession', newEmployee.profession);
      formData.append('email', newEmployee.email.trim());
      formData.append('bio', newEmployee.bio || '');
      formData.append('team', JSON.stringify(teamArray));
      
      // Add image (required)
      formData.append('image', file);

      const response = await fetch(`${API_BASE_URL}/employees`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        const formattedEmployee = {
          ...data.employee,
          id: data.employee._id || data.employee.id,
          image: data.employee.image || lead6,
        };
        
        setEmployees(prevEmployees => [...prevEmployees, formattedEmployee]);
        setIsAddMode(false);
        setNewEmployee({ name: '', profession: '', email: '', bio: '', teamMembers: [] });
        
        // Reset file input and preview
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setImagePreview(null);
        
        showNotification('Employee added successfully!', 'success');
      } else {
        // Show error message (could be validation error or limit reached)
        showNotification(data.message || 'Failed to add employee. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error adding employee:', err);
      showNotification('Failed to add employee. Please try again.', 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle image preview for add employee
  const handleImagePreview = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showNotification('Please select an image file', 'error');
        event.target.value = '';
        setImagePreview(null);
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        showNotification('Image size should be less than 10MB', 'error');
        event.target.value = '';
        setImagePreview(null);
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    } else {
      setImagePreview(null);
    }
  };

  // Handle delete employee
  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedEmployee.name}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setIsDeleting(true);
      const response = await fetch(`${API_BASE_URL}/employees/${selectedEmployee.id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        setEmployees(prevEmployees =>
          prevEmployees.filter(emp => emp.id !== selectedEmployee.id)
        );
        handleCloseModal();
        showNotification('Employee deleted successfully!', 'success');
      } else {
        showNotification(data.message || 'Failed to delete employee. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error deleting employee:', err);
      showNotification('Failed to delete employee. Please try again.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle input changes for new employee
  const handleNewEmployeeChange = (field, value) => {
    setNewEmployee(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle input changes in edit mode
  const handleInputChange = (field, value) => {
    setEditedEmployee(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Count words in bio (strip HTML tags for word count)
  const countWords = (text) => {
    if (!text) return 0;
    const stripped = text.replace(/<[^>]*>/g, ' ').trim();
    return stripped ? stripped.split(/\s+/).filter(word => word.length > 0).length : 0;
  };


  // ReactQuill toolbar configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link'
  ];

  // Get auth headers helper
  const getAuthHeaders = (headers = {}) => {
    if (!authToken) return headers;
    return {
      ...headers,
      Authorization: `Bearer ${authToken}`,
    };
  };

  // Fetch inquiries from database
  const fetchInquiries = async () => {
    setIsLoadingInquiries(true);
    try {
      const response = await fetch(`${API_BASE_URL}/lets-connect?page=1&limit=100`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        navigate('/adminlogin');
        return;
      }

      const result = await response.json();

      if (result.data) {
        // Filter out archived inquiries
        const activeInquiries = result.data.filter(inq => !inq.archivedAt);
        setInquiries(activeInquiries);
      } else {
        setInquiries([]);
      }
    } catch (err) {
      console.error('Error fetching inquiries:', err);
      setInquiries([]);
    } finally {
      setIsLoadingInquiries(false);
    }
  };

  // Handle opening inquiry modal
  const handleOpenInquiryModal = () => {
    setIsInquiryModalOpen(true);
    setSelectedInquiryId(null);
    fetchInquiries();
  };

  // Handle drag end - update positions
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    // Don't allow dragging placeholders
    if (active.id.toString().startsWith('placeholder-') || over.id.toString().startsWith('placeholder-')) {
      return;
    }

    const oldIndex = employees.findIndex(emp => emp.id === active.id);
    const newIndex = employees.findIndex(emp => emp.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Store original order for potential revert
    const originalEmployees = [...employees];

    // Update local state immediately for better UX
    const reorderedEmployees = arrayMove(employees, oldIndex, newIndex);
    setEmployees(reorderedEmployees);

    // Update positions in backend
    try {
      setIsUpdatingPositions(true);
      const positions = reorderedEmployees.map((emp, index) => ({
        id: emp.id,
        position: index,
      }));

      const response = await fetch(`${API_BASE_URL}/employees/positions`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ positions }),
      });

      const data = await response.json();

      if (!data.success) {
        // Revert on error
        setEmployees(originalEmployees);
        showNotification('Failed to update employee positions. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error updating positions:', err);
      // Revert on error
      setEmployees(originalEmployees);
      showNotification('Failed to update employee positions. Please try again.', 'error');
    } finally {
      setIsUpdatingPositions(false);
    }
  };

  // Handle send inquiry to employee
  const handleSendInquiry = async () => {
    if (!selectedInquiryId) {
      showNotification('Please select an inquiry to send', 'error');
      return;
    }

    if (!selectedEmployee) {
      showNotification('No employee selected', 'error');
      return;
    }

    const employeeId = selectedEmployee.id || selectedEmployee._id;
    if (!employeeId) {
      showNotification('Employee ID is missing', 'error');
      return;
    }

    try {
      setIsSendingInquiry(true);
      const response = await fetch(`${API_BASE_URL}/employees/${employeeId}/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inquiryId: selectedInquiryId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        showNotification('Inquiry sent successfully to employee!', 'success');
        setIsInquiryModalOpen(false);
        setSelectedInquiryId(null);
      } else {
        showNotification(data.message || 'Failed to send inquiry. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error sending inquiry:', err);
      showNotification('Failed to send inquiry. Please try again.', 'error');
    } finally {
      setIsSendingInquiry(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
        }
        @keyframes fadeInSlide {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        .animate-fadeOut {
          animation: fadeOut 0.3s ease-in;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-in;
        }
        .animate-fadeInSlide {
          animation: fadeInSlide 0.4s ease-out;
        }
        .view-transition {
          transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
        }
        .quill-editor-dark .ql-toolbar {
          background-color: #1D1C21;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px 8px 0 0;
          padding: 8px;
        }
        .quill-editor-dark .ql-container {
          background-color: transparent;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-top: none;
          border-radius: 0 0 8px 8px;
          color: white;
          font-size: 14px;
        }
        .quill-readonly .ql-toolbar.ql-snow {
          display: none !important;
        }
        .quill-readonly .ql-container {
          border-top: 1px solid rgba(255, 255, 255, 0.2) !important;
          border-radius: 8px !important;
        }
        .quill-editor-dark .ql-editor {
          color: white;
          min-height: 150px;
        }
        .quill-editor-dark .ql-editor.ql-blank::before {
          color: rgba(255, 255, 255, 0.5);
          font-style: normal;
        }
        .quill-editor-dark .ql-stroke {
          stroke: rgba(255, 255, 255, 0.7);
        }
        .quill-editor-dark .ql-fill {
          fill: rgba(255, 255, 255, 0.7);
        }
        .quill-editor-dark .ql-picker-label {
          color: rgba(255, 255, 255, 0.7);
        }
        .quill-editor-dark .ql-picker-options {
          background-color: #1D1C21;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
        }
        .quill-editor-dark .ql-picker-item:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .quill-editor-dark .ql-toolbar button:hover,
        .quill-editor-dark .ql-toolbar button:focus,
        .quill-editor-dark .ql-toolbar button.ql-active {
          color: #E68A40;
        }
        .quill-editor-dark .ql-toolbar button:hover .ql-stroke,
        .quill-editor-dark .ql-toolbar button:focus .ql-stroke,
        .quill-editor-dark .ql-toolbar button.ql-active .ql-stroke {
          stroke: #E68A40;
        }
        .quill-editor-dark .ql-toolbar button:hover .ql-fill,
        .quill-editor-dark .ql-toolbar button:focus .ql-fill,
        .quill-editor-dark .ql-toolbar button.ql-active .ql-fill {
          fill: #E68A40;
        }
        .quill-editor-dark .ql-container:focus-within {
          border-color: #E68A40;
        }
        .quill-editor-dark .ql-toolbar:focus-within {
          border-color: #E68A40;
        }
        .prose ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .prose ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .prose li {
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
          color: white;
        }
        .prose p {
          margin-top: 0.5rem;
          margin-bottom: 0.5rem;
          color: white;
        }
        .prose strong {
          font-weight: 600;
          color: white;
        }
        .prose em {
          font-style: italic;
          color: white;
        }
        .prose u {
          text-decoration: underline;
        }
        .prose a {
          color: #E68A40;
          text-decoration: underline;
        }
        .prose a:hover {
          color: #D67A30;
        }
        .prose h1, .prose h2, .prose h3 {
          color: white;
          font-weight: 600;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }
        .prose h1 {
          font-size: 1.5rem;
        }
        .prose h2 {
          font-size: 1.25rem;
        }
        .prose h3 {
          font-size: 1.125rem;
        }
      `}</style>
      <div 
        className="h-screen bg-[#151419] overflow-hidden" 
      >
        <div className="flex h-full transition-all duration-300">
          {/* Sidebar */}
          <div
            className="transition-all duration-300"
            style={{ width: isSidebarCollapsed ? collapsedWidth : fullWidth }}
          >
            <AdminSidebar 
              isCollapsed={isSidebarCollapsed} 
              onToggle={toggleSidebar} 
              activeLink={activeLink}
              setActiveLink={setActiveLink}
              onLogout={handleLogout}
              onNavigate={handleNavigation}
            />
          </div>

          {/* Main Content Area */}
          <div className="flex-1 p-8 overflow-y-auto overflow-x-auto transition-all duration-300 scrollbar-hide">
        
        {/* Top Header: Search Bar & View Toggle */}
        <div className="flex flex-col gap-5 mb-8">
          <div className="flex items-center justify-between gap-4 w-full">
              <div className="flex items-center bg-[#1D1C21] border border-[#2B2A30] rounded-2xl px-4 py-3 w-full sm:w-[420px] text-gray-400 shadow-[0_10px_40px_rgba(0,0,0,0.35)] select-none cursor-default">
              <FaSearch className="text-gray-500 mr-3" />
              <input 
                type="text" 
                placeholder="Search..." 
                className="bg-transparent flex-1 text-gray-200 placeholder-gray-500 focus:outline-none" 
              />
              <button className="text-gray-500 ml-3">
                <FaBars />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-[#E68A40] text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <FaBars className="text-xl" />
              </button>
              <button
                onClick={() => setViewMode('card')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'card' 
                    ? 'bg-[#E68A40] text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <FaTh className="text-xl" />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between w-full">
            <h1 className="text-3xl font-bold select-none cursor-default text-[#FFFFFF]">Employees</h1>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <p className="text-white text-lg">Loading employees...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <p className="text-red-400 text-lg mb-4">{error}</p>
              <button
                onClick={fetchEmployees}
                className="px-6 py-2 bg-[#E68A40] hover:bg-[#D67A30] text-white rounded-lg transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Card View */}
        {!loading && !error && (
          <div className={`${viewMode === 'card' ? 'block animate-fadeInSlide' : 'hidden'} w-full`}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={employees.map(emp => emp.id).concat(Array.from({ length: 8 - employees.length }, (_, i) => `placeholder-${employees.length + i}`))}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 w-full">
                  {Array.from({ length: 8 }, (_, index) => {
                    const employee = employees[index];
                    
                    // If no employee at this position, show placeholder
                    if (!employee) {
                      return (
                        <SortableEmployeeCard
                          key={`placeholder-${index}`}
                          employee={null}
                          index={index}
                          isPlaceholder={true}
                          onAddClick={handleAddClick}
                        />
                      );
                    }
                    
                    // Employee card
                    return (
                      <SortableEmployeeCard
                        key={employee.id}
                        employee={employee}
                        index={index}
                        isPlaceholder={false}
                        onCardClick={handleCardClick}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
            {isUpdatingPositions && (
              <div className="mt-4 text-center text-gray-400 text-sm">
                Updating positions...
              </div>
            )}
          </div>
        )}

        {/* Table View */}
        {!loading && !error && (
          <div className={`${viewMode === 'table' ? 'block animate-fadeInSlide' : 'hidden'}`}>
            <div className="bg-[#1D1C21] border border-white/10 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.45)] overflow-hidden select-none cursor-default">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-transparent">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider select-none cursor-default">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider select-none cursor-default">
                      Profession
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider select-none cursor-default">
                      Team
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {employees.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-8 text-center text-gray-400">
                        No employees found
                      </td>
                    </tr>
                  ) : (
                    employees.map((employee, index) => (
                <tr 
                  key={employee.id} 
                  onClick={() => handleCardClick(employee)}
                  className={`${index % 2 === 0 ? 'bg-[#1F1E23]' : 'bg-[#1D1C21]'} cursor-pointer hover:bg-[#2A2930] transition-colors`}
                >
                    <td className="px-6 py-4 whitespace-nowrap select-none cursor-default">
                      <div className="flex items-center gap-3">
                        <img 
                          src={employee.image} 
                          alt={employee.name}
                          className="w-10 h-10 rounded-full object-cover object-center"
                        />
                        <span className="text-sm text-white font-medium">{employee.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white select-none cursor-default">
                      {employee.profession}
                    </td>
                    <td className="px-6 py-4 text-sm text-white select-none cursor-default">
                      {employee.team && Array.isArray(employee.team) ? employee.team.join(', ') : ''}
                    </td>
                  </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        </div>
      </div>

      {/* Hidden File Input for Picture Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          if (isAddMode) {
            handleImagePreview(e);
          } else {
            handlePictureChange(e);
          }
        }}
        style={{ display: 'none' }}
      />

      {/* Image choice modal: original vs remove background (edit member) */}
      {imageChoiceModal.show && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-[#1D1C21] border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Choose image style</h3>
            <p className="text-gray-400 text-sm mb-5">
              Use the image as-is or remove the background (transparent) using our API.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => handleConfirmImageChoice(false)}
                className="flex-1 px-4 py-3 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/20 transition-colors"
              >
                Use original image
              </button>
              <button
                type="button"
                onClick={() => handleConfirmImageChoice(true)}
                className="flex-1 px-4 py-3 rounded-lg text-sm font-medium bg-[#E68A40] text-white hover:bg-[#D67A30] transition-colors"
              >
                Remove background
              </button>
            </div>
            <button
              type="button"
              onClick={() => setImageChoiceModal({ show: false, file: null })}
              className="mt-4 w-full py-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Employee Profile Modal */}
      {selectedEmployee && !isEditMode && (
        <div className={`fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-8 ${isClosing ? 'animate-fadeOut pointer-events-none' : 'animate-fadeIn'}`}>
        <div className={`bg-[#151419] rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide relative ${isClosing ? 'animate-slideDown' : 'animate-slideUp'}`}>
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <FaTimes className="w-6 h-6" />
            </button>

            {/* Profile Header */}
            <div className="flex items-start gap-6 mb-8 select-none cursor-default">
              <div className="relative w-32 h-32 rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #E68A40 0%, #D97C37 55%, #BF5B19 100%)' }}>
                <img 
                  src={selectedEmployee.image} 
                  alt={selectedEmployee.name}
                  className="w-full h-full object-cover relative z-10"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-2 select-none cursor-default">{selectedEmployee.name}</h2>
                <p className="text-[#E68A40] text-lg mb-2 select-none cursor-default">{selectedEmployee.profession}</p>
              </div>
            </div>

            {/* Main Content Card */}
            <div className="bg-[#1D1C21] border border-white/10 rounded-2xl p-6 select-none cursor-default">
              {/* Personal Info Section */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white select-none cursor-default">Personal Info</h3>
                  <div className="flex items-center gap-2 select-none cursor-default">
                    <button
                      onClick={handleOpenInquiryModal}
                      className="flex items-center gap-2 bg-[#E68A40] hover:bg-[#D67A30] text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                      Send Inquiry
                    </button>
                    <button
                      onClick={handleEditClick}
                      className="flex items-center gap-2 bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                    >
                      <FaEdit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={handleDeleteEmployee}
                      disabled={isDeleting}
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaTrash className="w-4 h-4" />
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-400 text-sm mb-1 select-none cursor-default">Full Name</p>
                    <p className="text-white select-none cursor-default">{selectedEmployee.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1 select-none cursor-default">Profession</p>
                    <p className="text-white select-none cursor-default">{selectedEmployee.profession}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1 select-none cursor-default">Email</p>
                    <p className="text-white select-none cursor-default">{selectedEmployee.email || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Bio Section */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white mb-2 select-none cursor-default">Bio</h3>
                <p className="text-gray-400 text-sm mb-3 select-none cursor-default">Training and Certificates</p>
                <div className="relative">
                  <div className="quill-editor-dark quill-readonly">
                    <ReactQuill
                      theme="snow"
                      value={selectedEmployee.bio || ''}
                      readOnly={true}
                      modules={{ toolbar: false }}
                      formats={quillFormats}
                    />
                  </div>
                  <p className="text-gray-400 text-sm mt-2 text-right">
                    {countWords(selectedEmployee.bio)} words
                  </p>
                </div>
              </div>

              {/* Team Section - Name, Role, Description or training/certificate */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 select-none cursor-default">Team</h3>
                <div className="space-y-4">
                  {selectedEmployee.team && selectedEmployee.team.length > 0 ? (
                    selectedEmployee.team.map((member, index) => {
                      const parsed = parseTeamLine(typeof member === 'string' ? member : `${member.name || ''} || ${member.role || member.position || ''} || ${member.description || ''}`);
                      return (
                        <div key={index} className="bg-white/5 rounded-lg border border-white/10 p-4 select-none cursor-default">
                          <p className="text-white font-semibold">{parsed.name || '—'}</p>
                          {parsed.role && <p className="text-[#E68A40] text-sm mt-0.5">{parsed.role}</p>}
                          {parsed.description && (
                            <div className="text-gray-400 text-sm mt-2 leading-relaxed quill-readonly" dangerouslySetInnerHTML={{ __html: parsed.description }} />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-400 select-none cursor-default">No team members assigned</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employee Edit Modal */}
      {selectedEmployee && isEditMode && editedEmployee && (
        <div className={`fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-8 ${isClosing ? 'animate-fadeOut pointer-events-none' : 'animate-fadeIn'}`}>
        <div className={`bg-[#151419] rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide relative ${isClosing ? 'animate-slideDown' : 'animate-slideUp'}`}>
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <FaTimes className="w-6 h-6" />
            </button>

            {/* Profile Header */}
            <div className="flex items-start gap-6 mb-8 select-none cursor-default">
              <div className="relative flex flex-col items-center gap-2">
                <div className="relative w-32 h-32 rounded-2xl overflow-hidden select-none cursor-default" style={{ background: 'linear-gradient(135deg, #E68A40 0%, #D97C37 55%, #BF5B19 100%)' }}>
                  <img 
                    src={editedEmployee.image} 
                    alt={editedEmployee.name}
                    className="w-full h-full object-cover relative z-10"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleChangePhotoClick}
                  disabled={uploadingImage}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-[#E68A40] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaImage className="w-4 h-4" />
                  {uploadingImage ? 'Uploading...' : 'Change image'}
                </button>
              </div>
              <div className="flex-1 select-none cursor-default">
                <h2 className="text-3xl font-bold text-white mb-2 select-none cursor-default">{editedEmployee.name}</h2>
                <p className="text-[#E68A40] text-lg mb-2 select-none cursor-default">{editedEmployee.profession}</p>
              </div>
            </div>

            {/* Main Content Card */}
            <div className="bg-[#1D1C21] border border-white/10 rounded-2xl p-6 select-none cursor-default">
              {/* Personal Info Section */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white mb-4 select-none cursor-default">Personal Info</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                  <label className="text-gray-400 text-sm mb-2 block">Full Name</label>
                    <input
                      type="text"
                      value={editedEmployee.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#E68A40]"
                    />
                  </div>
                  <div>
                  <label className="text-gray-400 text-sm mb-2 block">Profession</label>
                    <input
                      type="text"
                      value={editedEmployee.profession || ''}
                      onChange={(e) => handleInputChange('profession', e.target.value)}
                      className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#E68A40]"
                      placeholder="Enter profession"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="text-gray-400 text-sm mb-2 block">Email</label>
                  <input
                    type="email"
                    value={editedEmployee.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#E68A40]"
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              {/* Bio Section */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white mb-2">Bio</h3>
                <p className="text-gray-400 text-sm mb-3">Training and Certificates</p>
                <div className="relative">
                  <div className="quill-editor-dark">
                    <ReactQuill
                      theme="snow"
                      value={editedEmployee.bio || ''}
                      onChange={(value) => handleInputChange('bio', value)}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter bio, training, and certificates"
                    />
                  </div>
                  <p className="text-gray-400 text-sm mt-2 text-right">
                    {countWords(editedEmployee.bio)} words
                  </p>
                </div>
              </div>

              {/* Team Section - one block per member, scroll to edit each */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Team</h3>
                <p className="text-gray-400 text-sm mb-3">Add team members. Edit each one below (Name, Role, Description or training/certificate).</p>
                <div className="max-h-[320px] overflow-y-auto space-y-4 pr-2 scrollbar-admin">
                  {(editedEmployee.teamMembers || []).length === 0 ? (
                    <p className="text-gray-500 text-sm">No team members yet. Click &quot;Add team member&quot; below.</p>
                  ) : (
                    (editedEmployee.teamMembers || []).map((member, index) => (
                      <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-[#E68A40] font-medium text-sm">Member {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTeamMember(index)}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Name</label>
                          <input
                            type="text"
                            value={member.name || ''}
                            onChange={(e) => handleTeamMemberChange(index, 'name', e.target.value)}
                            className="w-full bg-transparent border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#E68A40]"
                            placeholder="e.g. Miguel Torres"
                          />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Role</label>
                          <input
                            type="text"
                            value={member.role || ''}
                            onChange={(e) => handleTeamMemberChange(index, 'role', e.target.value)}
                            className="w-full bg-transparent border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#E68A40]"
                            placeholder="e.g. Project Coordinator"
                          />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Description or training/certificate</label>
                          <div className="quill-editor-dark">
                            <ReactQuill
                              theme="snow"
                              value={member.description || ''}
                              onChange={(value) => handleTeamMemberChange(index, 'description', value)}
                              modules={quillModules}
                              formats={quillFormats}
                              placeholder="e.g. Experienced in construction project management. Use the toolbar for bullets and formatting."
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleAddTeamMember}
                  className="mt-3 px-4 py-2 rounded-lg border border-[#E68A40] text-[#E68A40] hover:bg-[#E68A40]/10 text-sm font-medium transition-colors"
                >
                  Add team member
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center mt-6 select-none cursor-default">
              <button
                onClick={handleDeleteEmployee}
                disabled={isDeleting}
                className="flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaTrash className="w-4 h-4" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
              <div className="flex gap-4">
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="px-6 py-2 bg-[#E68A40] hover:bg-[#D67A30] text-white rounded-lg transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {isAddMode && (
        <div className={`fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-8 ${isClosing ? 'animate-fadeOut pointer-events-none' : 'animate-fadeIn'}`}>
        <div className={`bg-[#151419] rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto scrollbar-hide relative ${isClosing ? 'animate-slideDown' : 'animate-slideUp'}`}>
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <FaTimes className="w-6 h-6" />
            </button>

            {/* Profile Header */}
            <div className="flex items-start gap-6 mb-8">
              <div 
                className="w-32 h-32 rounded-2xl bg-[#1D1C21] border border-white/10 flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#E68A40] transition-colors"
                onClick={handleChangePhotoClick}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-gray-400 text-sm text-center p-4">
                    <div>No Image</div>
                    <div className="text-xs text-red-400 mt-1">*Required</div>
                    <div className="text-xs text-gray-500 mt-2">Click to add</div>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-2">Add New Employee</h2>
                <button 
                  onClick={handleChangePhotoClick}
                  disabled={uploadingImage}
                  className="text-gray-400 hover:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                >
                  {uploadingImage ? 'Uploading...' : 'Add Photo *'}
                </button>
              </div>
            </div>

            {/* Main Content Card */}
            <div className="bg-[#1D1C21] border border-white/10 rounded-2xl p-6">
              {/* Personal Info Section */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white mb-4">Personal Info</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Full Name *</label>
                    <input
                      type="text"
                      value={newEmployee.name}
                      onChange={(e) => handleNewEmployeeChange('name', e.target.value)}
                      className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#E68A40]"
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Profession *</label>
                    <input
                      type="text"
                      value={newEmployee.profession}
                      onChange={(e) => handleNewEmployeeChange('profession', e.target.value)}
                      className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#E68A40]"
                      placeholder="Enter profession"
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="text-gray-400 text-sm mb-2 block">Email *</label>
                  <input
                    type="email"
                    value={newEmployee.email}
                    onChange={(e) => handleNewEmployeeChange('email', e.target.value)}
                    className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#E68A40]"
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              {/* Bio Section */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white mb-2">Bio</h3>
                <p className="text-gray-400 text-sm mb-3">Training and Certificates</p>
                <div className="relative">
                  <div className="quill-editor-dark">
                    <ReactQuill
                      theme="snow"
                      value={newEmployee.bio || ''}
                      onChange={(value) => handleNewEmployeeChange('bio', value)}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Enter bio, training, and certificates"
                    />
                  </div>
                  <p className="text-gray-400 text-sm mt-2 text-right">
                    {countWords(newEmployee.bio)} words
                  </p>
                </div>
              </div>

              {/* Team Section - one block per member */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Team</h3>
                <p className="text-gray-400 text-sm mb-3">Add team members. Edit each one below (Name, Role, Description or training/certificate).</p>
                <div className="max-h-[320px] overflow-y-auto space-y-4 pr-2 scrollbar-admin">
                  {(newEmployee.teamMembers || []).length === 0 ? (
                    <p className="text-gray-500 text-sm">No team members yet. Click &quot;Add team member&quot; below.</p>
                  ) : (
                    (newEmployee.teamMembers || []).map((member, index) => (
                      <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-[#E68A40] font-medium text-sm">Member {index + 1}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveNewTeamMember(index)}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Name</label>
                          <input
                            type="text"
                            value={member.name || ''}
                            onChange={(e) => handleNewTeamMemberChange(index, 'name', e.target.value)}
                            className="w-full bg-transparent border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#E68A40]"
                            placeholder="e.g. Miguel Torres"
                          />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Role</label>
                          <input
                            type="text"
                            value={member.role || ''}
                            onChange={(e) => handleNewTeamMemberChange(index, 'role', e.target.value)}
                            className="w-full bg-transparent border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#E68A40]"
                            placeholder="e.g. Project Coordinator"
                          />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs block mb-1">Description or training/certificate</label>
                          <div className="quill-editor-dark">
                            <ReactQuill
                              theme="snow"
                              value={member.description || ''}
                              onChange={(value) => handleNewTeamMemberChange(index, 'description', value)}
                              modules={quillModules}
                              formats={quillFormats}
                              placeholder="e.g. Experienced in construction project management. Use the toolbar for bullets and formatting."
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleAddNewTeamMember}
                  className="mt-3 px-4 py-2 rounded-lg border border-[#E68A40] text-[#E68A40] hover:bg-[#E68A40]/10 text-sm font-medium transition-colors"
                >
                  Add team member
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={handleCloseModal}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEmployee}
                disabled={uploadingImage}
                className="px-6 py-2 bg-[#E68A40] hover:bg-[#D67A30] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingImage ? 'Adding...' : 'Add Employee'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inquiry Modal */}
      {isInquiryModalOpen && selectedEmployee && (
        <div className={`fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-8 ${isClosing ? 'animate-fadeOut pointer-events-none' : 'animate-fadeIn'}`}>
          <div className={`bg-[#151419] rounded-2xl p-8 max-w-2xl w-full ${isClosing ? 'animate-slideDown' : 'animate-slideUp'}`}>
            {/* Close Button */}
            <button
              onClick={() => {
                setIsInquiryModalOpen(false);
                setSelectedInquiryId(null);
              }}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <FaTimes className="w-6 h-6" />
            </button>

            {/* Modal Header */}
            <div className="mb-6 select-none cursor-default">
              <h2 className="text-2xl font-bold text-white mb-2">Send Inquiry to {selectedEmployee.name}</h2>
              <p className="text-gray-400 text-sm">Select an inquiry from the database to send to {selectedEmployee.email}</p>
            </div>

            {/* Inquiries Table */}
            {isLoadingInquiries ? (
              <div className="text-center py-8">
                <p className="text-gray-400">Loading inquiries...</p>
              </div>
            ) : inquiries.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400">No inquiries available</p>
              </div>
            ) : (
              <div className="mb-6 max-h-96 overflow-y-auto scrollbar-hide select-none cursor-default">
                <table className="w-full text-left">
                  <thead className="bg-[#1D1C21] sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-gray-400 text-sm font-medium">Select</th>
                      <th className="px-4 py-3 text-gray-400 text-sm font-medium">Name</th>
                      <th className="px-4 py-3 text-gray-400 text-sm font-medium">Email</th>
                      <th className="px-4 py-3 text-gray-400 text-sm font-medium">Service</th>
                      <th className="px-4 py-3 text-gray-400 text-sm font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((inquiry) => (
                      <tr
                        key={inquiry._id || inquiry.id}
                        onClick={() => setSelectedInquiryId(inquiry._id || inquiry.id)}
                        className={`cursor-pointer border-b border-white/10 hover:bg-[#1D1C21] transition-colors ${
                          selectedInquiryId === (inquiry._id || inquiry.id) ? 'bg-[#E68A40]/20' : ''
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="radio"
                            name="inquiry"
                            checked={selectedInquiryId === (inquiry._id || inquiry.id)}
                            onChange={() => setSelectedInquiryId(inquiry._id || inquiry.id)}
                            className="w-4 h-4 text-[#E68A40] focus:ring-[#E68A40]"
                          />
                        </td>
                        <td className="px-4 py-3 text-white text-sm">{inquiry.fullName}</td>
                        <td className="px-4 py-3 text-white text-sm">{inquiry.email}</td>
                        <td className="px-4 py-3 text-white text-sm">
                          {SERVICE_INTEREST_LABELS[inquiry.serviceInterest] || inquiry.serviceInterest}
                        </td>
                        <td className="px-4 py-3 text-white text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            inquiry.status === 'new' ? 'bg-yellow-500/20 text-yellow-400' :
                            inquiry.status === 'contacted' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {inquiry.status === 'new' ? 'Pending' :
                             inquiry.status === 'contacted' ? 'Processing' :
                             'Completed'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Selected Inquiry Details */}
            {selectedInquiryId && (
              <div className="mb-6 p-4 bg-[#1D1C21] border border-[#E68A40]/30 rounded-lg select-none cursor-default">
                <h3 className="text-white font-semibold mb-2">Selected Inquiry:</h3>
                {(() => {
                  const selectedInquiry = inquiries.find(inq => (inq._id || inq.id) === selectedInquiryId);
                  if (!selectedInquiry) return null;
                  return (
                    <div className="text-sm text-gray-300 space-y-1">
                      <p><span className="text-gray-400">Client:</span> {selectedInquiry.fullName}</p>
                      <p><span className="text-gray-400">Email:</span> {selectedInquiry.email}</p>
                      <p><span className="text-gray-400">Service:</span> {SERVICE_INTEREST_LABELS[selectedInquiry.serviceInterest] || selectedInquiry.serviceInterest}</p>
                      <p className="text-gray-400 mt-2">Description:</p>
                      <p className="text-white text-xs bg-[#151419] p-2 rounded max-h-32 overflow-y-auto scrollbar-hide">
                        {selectedInquiry.description}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 mt-6 select-none cursor-default">
              <button
                onClick={() => {
                  setIsInquiryModalOpen(false);
                  setSelectedInquiryId(null);
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInquiry}
                disabled={isSendingInquiry}
                className="px-6 py-2 bg-[#E68A40] hover:bg-[#D67A30] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSendingInquiry ? 'Sending...' : 'Send Inquiry'}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>

      {/* Custom Notification Toast */}
      {notification && (
        <div 
          className={`fixed bottom-6 right-6 z-[100] px-6 py-4 rounded-2xl shadow-2xl backdrop-blur-md transform transition-all duration-300 ease-out ${
            notification.type === 'success' 
              ? 'bg-green-500/20 border border-green-500/40 text-green-200' 
              : 'bg-red-500/20 border border-red-500/40 text-red-200'
          } ${notification ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}
          style={{
            animation: 'slideInRight 0.3s ease-out',
          }}
        >
          <div className="flex items-center gap-3">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <p className="text-sm font-medium">{notification.message}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Employees;

